import type { AppOpiniao, AppPost } from '@/contentTypes';
import moment from 'moment';
import _ from 'lodash';
import categories, { type Category } from '@/configs/categories';
import { decode } from 'he';
import { useIndexedDBStore } from './useIndexDB';
import { fetchOpinionById, fetchOpinionUpdatedAt, fetchPost, fetchPostUpdatedAt } from '@/api/posts';
import { CACHE_CONSTANTS, DATE_CONSTANTS } from '@/configs/constants';

export function usePost(post: Partial<AppPost>, category: Category | null = null) {
    const momentDate = parseApiDate(post.date ?? post.publishedAt);

    const relativeDate = getRelativeDate(post.date ?? post.publishedAt);

    const dayDate = momentDate.format('DD/MM/YYYY');

    const imgSrc = post.featuredImageUrl || '';
    const thumbnailSrc = imgSrc;

    const title = decode(post.title || '');

    const excerpt = decode(post.excerpt || '');

    const content = decode(post.contentHtml || '');

    const url = post?.link || '';

    const author = post?.authorName || '';
    const authorImgUrl = post?.authorImageUrl || '';

    const categoryIds = post.categoryWpIds ?? post.categories ?? [];
    const _category =
        category ||
        (categories.find((c) => Array.isArray(categoryIds) && categoryIds.includes(c.wordpressId)) as Category) ||
        categories.find((c) => c.value === 'news');

    const color = _category?.color || 'news';

    const colorVariable = `var(--v-theme-${color})`;

    const categoryName = _category?.title || 'News';

    const categoryTextClass = _category?.textColor || 'tw-text-news';

    const categoryBgClass = _category?.backgroundColor || 'tw-bg-news';

    const categoryFgClass = _category?.foregroundColor || 'tw-text-white';

    const toPostPage = _.get(_category, 'toPostPage', 'NewsPage');

    return {
        id: post.id ?? post.wpId,
        relativeDate,
        dayDate,
        excerpt,
        content,
        imgSrc,
        thumbnailSrc,
        title,
        color,
        colorVariable,
        category: _category,
        categoryName,
        categoryTextClass,
        categoryBgClass,
        categoryFgClass,
        toPostPage,
        url,
        author,
        authorImgUrl,
    };
}

export async function getPostsFromCache(query?: { search?: string; category?: number; pageSize?: number }): Promise<AppPost[]> {
    const indexDB = useIndexedDBStore('posts');
    const categoryId = query?.category;
    let cachedPosts = await indexDB.getAll();
    const pageSize = query?.pageSize || cachedPosts.length;

    if (typeof categoryId === 'number') {
        cachedPosts = cachedPosts.filter((post: AppPost) => {
            const ids = post.categoryWpIds ?? post.categories ?? [];
            return ids.includes(categoryId);
        });
    }

    cachedPosts = _.orderBy(cachedPosts, ['date'], ['desc']).slice(0, pageSize);

    return cachedPosts;
}

export async function getOpinionsFromCache(): Promise<AppOpiniao[]> {
    const indexDB = useIndexedDBStore('opinions');
    let cachedOpinions = await indexDB.getAll();

    cachedOpinions = _.orderBy(cachedOpinions, ['date'], ['desc']);

    return cachedOpinions;
}

async function checkPostUpdated(cachedPost: AppPost, id: number): Promise<boolean> {
    try {
        const response = await fetchPostUpdatedAt(id);
        const updatedAt = response.data?.updatedAt ?? response.data?.updated_at;
        if (updatedAt) {
            const apiUpdatedAt = new Date(updatedAt).getTime();
            const cachedUpdatedAt = cachedPost.updatedAt
                ? new Date(cachedPost.updatedAt).getTime()
                : null;

            if (!cachedUpdatedAt) {
                return true;
            }

            return apiUpdatedAt > cachedUpdatedAt;
        }
    } catch (error) {
        console.error('Error checking post update:', error);
    }
    return false;
}

export async function getPostById(id: number): Promise<AppPost | null> {
    const indexDB = useIndexedDBStore('posts');

    const cachedPost: AppPost = await indexDB.get(id);
    if (cachedPost) {
        const wasUpdated = await checkPostUpdated(cachedPost, id);
        if (wasUpdated) {
            await indexDB.delete(id);
            const response = await fetchPost(id);
            if (response.data) {
                const freshPost = response.data as AppPost;
                await indexDB.set(freshPost);
                return freshPost;
            }
        }
        return cachedPost;
    }

    const { data } = await fetchPost(id);

    if (data) {
        return data as AppPost;
    }
    return null;
}

async function checkOpinionUpdated(cachedOpinion: AppOpiniao, id: number): Promise<boolean> {
    try {
        const response = await fetchOpinionUpdatedAt(id);
        const updatedAt = response.data?.updatedAt ?? response.data?.updated_at;
        if (updatedAt) {
            const apiUpdatedAt = new Date(updatedAt).getTime();
            const cachedUpdatedAt = cachedOpinion.updatedAt
                ? new Date(cachedOpinion.updatedAt).getTime()
                : null;

            if (!cachedUpdatedAt) {
                return true;
            }

            return apiUpdatedAt > cachedUpdatedAt;
        }
    } catch (error) {
        console.error('Error checking opinion update:', error);
    }
    return false;
}

export async function getOpinionById(id: number): Promise<AppOpiniao | null> {
    const indexDB = useIndexedDBStore('opinions');

    const cachedOpinion: AppOpiniao = await indexDB.get(id);
    if (cachedOpinion) {
        const wasUpdated = await checkOpinionUpdated(cachedOpinion, id);
        if (wasUpdated) {
            await indexDB.delete(id);
            const response = await fetchOpinionById(id);
            if (response.data) {
                const freshOpinion = response.data as AppOpiniao;
                await indexDB.set(freshOpinion);
                return freshOpinion;
            }
        }
        return cachedOpinion;
    }

    const { data } = await fetchOpinionById(id);

    if (data) {
        return data as AppOpiniao;
    }
    return null;
}

export function parsePosts(posts: AppPost[] | AppPost): Partial<AppPost>[] | Partial<AppPost> {
    return posts;
}

export async function downloadImages(posts: AppPost[]) {
    const urls = new Set<string>();

    posts.forEach((post) => {
        if (post.featuredImageUrl) {
            urls.add(post.featuredImageUrl);
        }
    });

    const cache = await caches.open(CACHE_CONSTANTS.CACHE_NAMES.IMAGES);
    const urlsArray = Array.from(urls);

    for (const url of urlsArray) {
        const cached = await cache.match(url as string);
        if (!cached) {
            try {
                const response = await fetch(url as string, { mode: 'no-cors' });
                await cache.put(url as string, response);
            } catch (err) {
                console.warn(`Failed to cache: ${url}`, err);
            }
        }
    }

    const cachedKeys = await cache.keys();
    const cachedUrls = cachedKeys.map((request) => request.url);
    const urlsToRemove = cachedUrls.filter((cachedUrl) => !urls.has(cachedUrl));
    for (const url of urlsToRemove) {
        await cache.delete(url);
    }
}

export async function downloadImagesFromOpinions(opinions: AppOpiniao[]) {
    const urls = new Set<string>();

    opinions.forEach((opinion) => {
        if (opinion.authorImageUrl) {
            urls.add(opinion.authorImageUrl);
        }
    });

    const cache = await caches.open(CACHE_CONSTANTS.CACHE_NAMES.OPINION_IMAGES);
    const urlsArray = Array.from(urls);

    for (const url of urlsArray) {
        const cached = await cache.match(url as string);
        if (!cached) {
            try {
                const response = await fetch(url as string, { mode: 'no-cors' });
                await cache.put(url as string, response);
            } catch (err) {
                console.warn(`Failed to cache: ${url}`, err);
            }
        }
    }

    const cachedKeys = await cache.keys();
    const cachedUrls = cachedKeys.map((request) => request.url);
    const urlsToRemove = cachedUrls.filter((cachedUrl) => !urls.has(cachedUrl));
    for (const url of urlsToRemove) {
        await cache.delete(url);
    }
}

/**
 * API dates are ISO-8601 from Site; legacy naive MySQL datetimes treated as UTC.
 */
function parseApiDate(date: any) {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
        return moment.utc(date).local();
    }
    return moment(date);
}

export function getRelativeDate(date: any): string {
    const momentDate = parseApiDate(date);
    if (moment(new Date()).diff(momentDate, 'days') <= DATE_CONSTANTS.RELATIVE_TIME_THRESHOLD_DAYS) {
        return momentDate.fromNow();
    }

    return momentDate.format('DD/MM/YYYY, HH:mm');
}
